import type { Id, TxType } from '../data/types';

/**
 * Stato locale della vista Transazioni serializzato nell'URL.
 * Il testo libero è escluso di proposito: può contenere dati sensibili.
 */

export type SortKey = 'date' | 'description' | 'amount' | 'type' | 'account' | 'category' | 'group';
export type SortRule = { key: SortKey; direction: 'asc' | 'desc' };
export type TriState = 'all' | 'yes' | 'no';

export interface TransactionViewState {
  minAmount: string;
  maxAmount: string;
  sign: 'all' | 'positive' | 'negative';
  types: TxType[];
  counterAccountId: Id;
  parentCategoryId: Id;
  hasDescription: TriState;
  weekend: TriState;
  daysOfWeek: number[];
  transactionId: string;
  transferId: string;
  pageSize: number;
  sortRules: SortRule[];
}

export const PAGE_SIZES = [10, 25, 50, 100] as const;
export const DEFAULT_PAGE_SIZE = 25;
export const DEFAULT_SORT: readonly SortRule[] = [{ key: 'date', direction: 'desc' }];
const SORT_KEYS: readonly SortKey[] = [
  'date',
  'description',
  'amount',
  'type',
  'account',
  'category',
  'group',
];
const TX_TYPES: readonly TxType[] = ['income', 'expense', 'transfer_in', 'transfer_out'];
const AMOUNT_PATTERN = /^\d+(?:[.,]\d{1,2})?$/;

/** Converte un importo in euro scritto dall'utente (`12`, `12,5`, `12.50`) in centesimi. */
export function parseEuroCents(value: string): number | undefined {
  const trimmed = value.trim();
  if (!AMOUNT_PATTERN.test(trimmed)) return undefined;
  return Math.round(Number(trimmed.replace(',', '.')) * 100);
}

export function parseSort(value: string | null): SortRule[] {
  if (!value) return [...DEFAULT_SORT];
  const seen = new Set<SortKey>();
  const rules = value.split(',').flatMap((part): SortRule[] => {
    const [key, direction] = part.split(':');
    if (!SORT_KEYS.includes(key as SortKey) || seen.has(key as SortKey)) return [];
    if (direction !== 'asc' && direction !== 'desc') return [];
    seen.add(key as SortKey);
    return [{ key: key as SortKey, direction }];
  });
  return rules.length ? rules : [...DEFAULT_SORT];
}

function oneOf<T extends string>(value: string | null, allowed: readonly T[], fallback: T): T {
  return allowed.includes(value as T) ? (value as T) : fallback;
}

export function parseTransactionViewState(
  params: URLSearchParams,
  known: { accountIds: ReadonlySet<Id>; parentCategoryIds: ReadonlySet<Id> },
): TransactionViewState {
  const amount = (raw: string | null) => (raw && parseEuroCents(raw) !== undefined ? raw : '');
  const pageSize = Number(params.get('pageSize'));
  const counter = params.get('counter') ?? '';
  const parentCategory = params.get('parentCategory') ?? '';
  return {
    minAmount: amount(params.get('min')),
    maxAmount: amount(params.get('max')),
    sign: oneOf(params.get('sign'), ['positive', 'negative'], 'all'),
    types: Array.from(
      new Set(
        (params.get('types') ?? '')
          .split(',')
          .filter((type): type is TxType => TX_TYPES.includes(type as TxType)),
      ),
    ),
    counterAccountId: known.accountIds.has(counter) ? counter : '',
    parentCategoryId: known.parentCategoryIds.has(parentCategory) ? parentCategory : '',
    hasDescription: oneOf(params.get('description'), ['yes', 'no'], 'all'),
    weekend: oneOf(params.get('weekend'), ['yes', 'no'], 'all'),
    daysOfWeek: Array.from(
      new Set(
        (params.get('days') ?? '')
          .split(',')
          .filter((day) => /^[0-6]$/.test(day))
          .map(Number),
      ),
    ).sort((left, right) => left - right),
    transactionId: params.get('transaction') ?? '',
    transferId: params.get('transfer') ?? '',
    pageSize: (PAGE_SIZES as readonly number[]).includes(pageSize) ? pageSize : DEFAULT_PAGE_SIZE,
    sortRules: parseSort(params.get('sort')),
  };
}

/** Scrive lo stato locale su `params`, omettendo i valori di default. */
export function writeTransactionViewState(
  params: URLSearchParams,
  state: TransactionViewState,
): void {
  if (state.minAmount) params.set('min', state.minAmount);
  if (state.maxAmount) params.set('max', state.maxAmount);
  if (state.sign !== 'all') params.set('sign', state.sign);
  if (state.types.length) params.set('types', state.types.join(','));
  if (state.counterAccountId) params.set('counter', state.counterAccountId);
  if (state.parentCategoryId) params.set('parentCategory', state.parentCategoryId);
  if (state.hasDescription !== 'all') params.set('description', state.hasDescription);
  if (state.weekend !== 'all') params.set('weekend', state.weekend);
  if (state.daysOfWeek.length) params.set('days', state.daysOfWeek.join(','));
  if (state.transactionId) params.set('transaction', state.transactionId);
  if (state.transferId) params.set('transfer', state.transferId);
  params.set('sort', state.sortRules.map((rule) => `${rule.key}:${rule.direction}`).join(','));
  params.set('pageSize', String(state.pageSize));
}
